<template>
  <div class="buffet-setup-bar" role="region" aria-label="Confirm buffet plan">
    <div class="container-xl buffet-setup-bar__row">
      <button
        type="button"
        class="buffet-setup-bar__back scan-touch-target"
        :aria-label="$t('back_to_plans')"
        @click="$emit('back')"
      >
        <v-icon size="16">fal fa-arrow-left</v-icon>
      </button>

      <div class="buffet-setup-bar__main">
        <div class="buffet-setup-bar__plan" v-if="plan">
          <span class="buffet-setup-bar__plan-name text-truncate">{{
            plan.name
          }}</span>
          <span class="buffet-setup-bar__plan-price">
            {{ formatMoney(plan.price) }} ₭
            <span class="buffet-setup-bar__plan-price-suffix">
              · {{ $t("buffet_plan_price") }}
            </span>
          </span>
        </div>

        <div
          v-if="!readonly"
          class="buffet-setup-bar__persons"
          role="group"
          aria-label="Guests"
        >
          <button
            type="button"
            class="buffet-setup-bar__step scan-touch-target"
            :aria-label="`${$t('buffet_persons')} -`"
            :disabled="persons <= 1 || disabled"
            @click="decrement"
          >
            <v-icon size="14">fal fa-minus</v-icon>
          </button>
          <div class="buffet-setup-bar__persons-value">
            <span class="buffet-setup-bar__persons-count">{{ persons }}</span>
            <span class="buffet-setup-bar__persons-label">
              {{ $t("buffet_persons") }}
            </span>
          </div>
          <button
            type="button"
            class="buffet-setup-bar__step buffet-setup-bar__step--plus scan-touch-target"
            :aria-label="`${$t('buffet_persons')} +`"
            :disabled="disabled"
            @click="increment"
          >
            <v-icon size="14" color="white">fal fa-plus</v-icon>
          </button>
        </div>

        <div
          v-else
          class="buffet-setup-bar__readonly"
          role="status"
          :aria-label="$t('please_ask_staff_to_open_table')"
        >
          <v-icon size="14" class="buffet-setup-bar__readonly-icon">
            fal fa-info-circle
          </v-icon>
          <span class="buffet-setup-bar__readonly-text">
            {{ $t("staff_will_open_table") }}
          </span>
        </div>
      </div>

      <button
        v-if="!readonly"
        type="button"
        class="buffet-setup-bar__confirm"
        :disabled="!plan || persons < 1 || disabled || loading"
        @click="onConfirm"
      >
        <v-progress-circular
          v-if="loading"
          indeterminate
          size="18"
          width="2"
          color="white"
        />
        <template v-else>
          <v-icon size="14" class="buffet-setup-bar__confirm-icon">
            fal fa-check
          </v-icon>
          <span>{{ $t("confirm_plan") }}</span>
        </template>
      </button>
    </div>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "BuffetSetupBottomBar",
  mixins: [myMixin],
  props: {
    plan: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    minPersons: { type: Number, default: 1 },
    initialPersons: { type: Number, default: 1 },
  },
  data() {
    return {
      persons: Math.max(this.minPersons, Number(this.initialPersons) || 1),
    };
  },
  watch: {
    plan(newPlan) {
      if (newPlan) {
        this.persons = Math.max(
          this.minPersons,
          Number(this.initialPersons) || 1
        );
      }
    },
  },
  methods: {
    formatMoney(n) {
      return this.$options.filters.NumberFormat(Number(n || 0));
    },
    increment() {
      if (this.disabled) return;
      this.persons = Math.max(this.minPersons, Number(this.persons) || 0) + 1;
    },
    decrement() {
      if (this.disabled) return;
      const next = Math.max(this.minPersons, Number(this.persons) || 0) - 1;
      this.persons = Math.max(this.minPersons, next);
    },
    onConfirm() {
      if (!this.plan) return;
      const persons = Math.max(this.minPersons, Number(this.persons) || 1);
      this.$emit("confirm", {
        buffet_plan_id: this.plan.id,
        persons,
      });
    },
  },
};
</script>

<style scoped>
.buffet-setup-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 6;
  background: rgba(255, 255, 255, 0.96);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  border-top: 0.5px solid var(--qr-card-border);
  box-shadow: 0 -8px 24px rgba(40, 28, 16, 0.08);
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
}

.buffet-setup-bar__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.buffet-setup-bar__back {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 0.5px solid var(--qr-card-border);
  background: rgba(40, 28, 16, 0.04);
  color: var(--qr-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
}

.buffet-setup-bar__back:focus-visible {
  outline: 2px solid var(--qr-accent);
  outline-offset: 2px;
}

.buffet-setup-bar__main {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.buffet-setup-bar__plan {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.buffet-setup-bar__plan-name {
  font-weight: 800;
  font-size: 13px;
  color: var(--qr-text);
  letter-spacing: -0.2px;
}

.buffet-setup-bar__plan-price {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
}

.buffet-setup-bar__plan-price-suffix {
  color: var(--qr-muted);
  font-weight: 500;
}

.buffet-setup-bar__persons {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--qr-accent);
  border-radius: 999px;
  padding: 2px;
  gap: 2px;
}

.buffet-setup-bar__step {
  width: 36px;
  height: 36px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: var(--qr-text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.buffet-setup-bar__step:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.buffet-setup-bar__step--plus {
  background: var(--qr-accent);
  color: #fff;
}

.buffet-setup-bar__persons-value {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0 6px;
  line-height: 1;
}

.buffet-setup-bar__persons-count {
  font-size: 14px;
  font-weight: 800;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
}

.buffet-setup-bar__persons-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: var(--qr-muted);
  margin-top: 1px;
}

.buffet-setup-bar__confirm {
  flex-shrink: 0;
  min-width: 110px;
  height: 44px;
  border-radius: 14px;
  border: 0;
  background: var(--qr-accent);
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: -0.1px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px;
  box-shadow: var(--qr-control-shadow);
  touch-action: manipulation;
}

.buffet-setup-bar__confirm:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.buffet-setup-bar__confirm:focus-visible {
  outline: 2px solid var(--qr-accent);
  outline-offset: 2px;
}

.buffet-setup-bar__confirm-icon {
  color: #fff !important;
}

.buffet-setup-bar__readonly {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: var(--qr-accent-faint);
  border: 0.5px solid var(--qr-accent-border);
  color: var(--qr-text);
  max-width: 100%;
}

.buffet-setup-bar__readonly-icon {
  color: var(--qr-accent);
  flex-shrink: 0;
}

.buffet-setup-bar__readonly-text {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.1px;
}

@media (max-width: 599px) {
  .buffet-setup-bar__plan {
    display: none;
  }
  .buffet-setup-bar__confirm span {
    display: none;
  }
  .buffet-setup-bar__confirm {
    min-width: 56px;
    padding: 0;
    width: 56px;
  }
  .buffet-setup-bar__main {
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .buffet-setup-bar__confirm,
  .buffet-setup-bar__step {
    transition: none !important;
  }
}
</style>
